import {
  Box,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Text,
  ModalCloseButton,
} from '@chakra-ui/react';
import { compositionGroupMatrixApi } from '@texas/api/endpoints/compositionGroup/compositionGroupMatrixApi';
import { CompositionGroup } from '@texas/api/endpoints/compositionGroup/compositionGroupApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { Icons } from '@texas/components/shared/Icons';
import { request } from '@texas/utils/helpers/httpHelpers';
import { tagEvents } from '@texas/components/tags/events';
import { EntityType } from '@texas/api/endpoints/trackerApi';
import { useSignalSubscriptionEvents } from '@texas/components/shared/hooks/useSignalSubscriptionEvents';
import { useEffect } from 'react';
import { TagWithTooltip } from '@texas/components/shared/TagWithTooltip';
import { useTranslation } from 'react-i18next';
import { compositionVersionEvents } from '../events';
import { CompositionGroupView } from '../../compositions/shared/group/CompositionGroupView';
import { useValueDisclosure } from '@texas/hooks/useValueDisclosure';
import { VersionsMenu } from '../shared/VersionsMenu';
import { CompositionGroupHistoryModal } from '../../history/CompositionGroupHistoryModal';
import { CountIcon } from '@texas/components/shared/CountIcon';
import { ProductApprovalState } from '@texas/api/endpoints/productApprovalsApi';
import { convertToEnum } from '@texas/utils/helpers/enumHelpers';
import { productApprovalEvents } from '@texas/components/shared/productApproval/events';
import { ArchiveCompositionGroupModal } from '../shared/ArchiveCompositionGroupModal';
import { LayoutViewerModal } from '../shared/LayoutViewerModal';
import { GeneratePaFromGroupsModal } from '@texas/components/shared/productApproval/GeneratePaFromGroupsModal';

export function ControllerVersionsList({
  optionId,
  isReady,
  supplierIds,
  branchIds,
}: {
  optionId: number;
  isReady: boolean;
  supplierIds?: number[];
  branchIds?: number[];
}) {
  const { t } = useTranslation();
  const { value, isOpen, onClose, onOpen } = useValueDisclosure<{
    id: number;
    readOnly: boolean;
    isArchived: boolean;
  }>();
  const {
    value: archiveValue,
    isOpen: isArchiveOpen,
    onClose: onArchiveClose,
    onOpen: onArchiveOpen,
  } = useValueDisclosure<number>();
  const {
    isOpen: isHistoryOpen,
    onClose: onHistoryClose,
    onOpen: onHistoryOpen,
    value: historyValue,
  } = useValueDisclosure<{
    id: number;
    name: string;
  }>();

  const {
    value: layoutFileId,
    isOpen: isLayoutOpen,
    onClose: onLayoutClose,
    onOpen: onLayoutOpen,
  } = useValueDisclosure<number>();

  const {
    value: samplingGroupIds,
    isOpen: isSamplingOpen,
    onClose: onSamplingClose,
    onOpen: onSamplingOpen,
  } = useValueDisclosure<number[]>();

  const { data: versions, refetch } = useApiResource(
    compositionGroupMatrixApi.getOptionControllerVersions,
  );

  const { request: patchVersionsRequest, loading: isPatching } = useApiRequest(
    compositionGroupMatrixApi.patchControllerVersion,
  );

  const patch = async (controllerId: number) => {
    await request(patchVersionsRequest, [controllerId], () => {
      compositionVersionEvents.onVersionsPatched.dispatch();
      refetch(optionId);
    });
  };

  useEffect(() => {
    refetch(optionId);
  }, [refetch, optionId]);

  useEffect(() => {
    const refreshControllerVersion = (version: CompositionGroup) => {
      if (version.basedOnGroupId === optionId) void refetch(optionId);
    };
    const unsubscribeCreated =
      compositionVersionEvents.onControllerVersionCreated.subscribe(
        refreshControllerVersion,
      );
    const unsubscribeRestored =
      compositionVersionEvents.onControllerVersionRestored.subscribe(
        refreshControllerVersion,
      );
    return () => {
      unsubscribeCreated();
      unsubscribeRestored();
    };
  }, [optionId, refetch]);

  useSignalSubscriptionEvents({
    triggerOnEvent: () => {
      void refetch(optionId);
    },
    signalEvents: [
      productApprovalEvents.productApprovalsCreated,
      tagEvents.tagUpdated,
    ],
  });

  useEffect(
    () =>
      tagEvents.tagLinksUpdated.subscribe((event) => {
        if (
          event.entityType === EntityType.Supplier &&
          versions?.some((version) => version.supplierId === event.entityId)
        )
          void refetch(optionId);
      }),
    [optionId, refetch, versions],
  );

  return (
    <>
      {versions
        ?.filter(
          (v) =>
            (!supplierIds?.length ||
              (v.supplierId !== null && supplierIds.includes(v.supplierId))) &&
            (!branchIds?.length ||
              (v.branchId !== null && branchIds.includes(v.branchId))),
        )
        .map((v) => {
          const pas = getControllerVersionPas(v);
          return (
            <Flex direction="column" key={v.id} alignItems="center" w="100%">
              <Box
                w="3px"
                h="10px"
                background="whiteAlpha.200"
                _light={{ background: 'blackAlpha.100' }}
              />
              <VersionsMenu
                version={v}
                basedOnGroupVersion={v.basedOnGroupVersion!}
                onArchiveOpen={onArchiveOpen}
                onOpen={(id: number, readOnly: boolean, isArchived: boolean) =>
                  onOpen({
                    id,
                    readOnly: readOnly || v.completedDate !== null,
                    isArchived,
                  })
                }
                patchCount={v.patchCount}
                onHistoryOpen={(id, name) => onHistoryOpen({ id, name })}
                onPatch={isReady ? (id: number) => patch(id) : undefined}
                onViewLayout={(fileIdentifier) => onLayoutOpen(fileIdentifier)}
                onInitiateSampling={(groupIds) => onSamplingOpen(groupIds)}
                menuButtonVariant="outlined"
                loading={isPatching}
              >
                <Flex
                  alignItems="center"
                  justifyContent="space-around"
                  gap={3}
                  maxW={72}
                >
                  <Flex direction="column" whiteSpace="normal">
                    <Text>
                      {v.supplierName} ({v.branchIdentifier})
                    </Text>
                    {!!v.supplierTags?.length && (
                      <Flex gap={1} wrap="wrap" mt={1}>
                        {v.supplierTags.map((tag) => (
                          <TagWithTooltip key={tag.id} tag={tag} />
                        ))}
                      </Flex>
                    )}
                    <Text variant="main">
                      {t('compositionGroup.version.versionNr', {
                        nr: v.version,
                      })}
                    </Text>
                  </Flex>
                  <Flex gap={2}>
                    <CountIcon
                      icon={<Icons.Clock boxSize={4} />}
                      count={pas.pendingPas}
                    />
                    <CountIcon
                      icon={<Icons.CheckCircleOutline boxSize={4} />}
                      count={pas.acceptedPas}
                    />
                  </Flex>
                </Flex>
              </VersionsMenu>
            </Flex>
          );
        })}

      {archiveValue && (
        <ArchiveCompositionGroupModal
          groupId={archiveValue}
          isOpen={isArchiveOpen}
          onClose={onArchiveClose}
          onArchived={() => refetch(optionId)}
        />
      )}
      {historyValue && (
        <CompositionGroupHistoryModal
          isOpen={isHistoryOpen}
          onClose={onHistoryClose}
          compositionGroupId={historyValue.id}
          name={historyValue.name}
        />
      )}
      {layoutFileId && (
        <LayoutViewerModal
          isOpen={isLayoutOpen}
          onClose={onLayoutClose}
          fileId={layoutFileId}
        />
      )}
      {samplingGroupIds && (
        <GeneratePaFromGroupsModal
          isOpen={isSamplingOpen}
          onClose={onSamplingClose}
          groupIds={samplingGroupIds}
        />
      )}
      <Modal isCentered={true} isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent width="auto" maxW="fit-content">
          <ModalCloseButton />
          <ModalBody>
            {value && (
              <VersionInnerModal
                id={value.id}
                readOnly={value.readOnly}
                isArchived={value.isArchived}
              />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}

function VersionInnerModal({
  id,
  readOnly,
  isArchived,
}: {
  id: number;
  readOnly: boolean;
  isArchived: boolean;
}) {
  return (
    <>
      <CompositionGroupView
        variantId={0}
        compositionGroupId={id}
        componentSettings={{
          expandedDefault: true,
          compositionArchiveRestore: 'hidden',
          addMaterials: 'hidden',
          materialsMode: 'only-edit',
          removeMaterials: 'hidden',
          colors: 'hidden',
          dimensionMode: 'read-only-group',
          readOnly: readOnly || isArchived,
        }}
      />
    </>
  );
}

function getControllerVersionPas(controllerVersion: CompositionGroup): {
  pendingPas: number;
  acceptedPas: number;
} {
  const pendingPas =
    controllerVersion.controlledGroupsProductApprovals?.filter(
      (x) =>
        convertToEnum(ProductApprovalState, x.state) ==
        ProductApprovalState.Pending,
    ).length ?? 0;
  const acceptedPas =
    controllerVersion.controlledGroupsProductApprovals?.filter(
      (x) =>
        convertToEnum(ProductApprovalState, x.state) ==
        ProductApprovalState.Accepted,
    ).length ?? 0;

  return { pendingPas, acceptedPas };
}
